import { inject } from 'vue';
import Honeybadger from '@honeybadger-io/js';

export function createHoneybadger(options)
{
    const honeybadger = new VueHoneybadger(options);

    // Filter out MetaMask errors.
    // These come from unhandled errors in a badly written Chrome extension that
    // some customers must have installed. They keep popping up in Honeybadger.
    honeybadger.beforeNotify(notice =>
    {
        // Flagged in the axios interceptor (src/axios.js)
        if(notice?.ignoreHoneybadger)
        {
            return false;
        }

        const metamaskErrors = [
            'wallet must has at least one account',
            'wallet must have at least one account',
        ];
        for(const error of metamaskErrors)
        {
            if(notice?.message?.includes(error))
            {
                return false;
            }
        }
    });

    return honeybadger;
}

export class VueHoneybadger
{
    constructor(options = {})
    {
        this._honeybadger = Honeybadger.configure(options);
    }

    install(app)
    {
        app.config.globalProperties.$honeybadger = this._honeybadger;
        app.provide('honeybadger', this._honeybadger);

        const chainedErrorHandler = app.config.errorHandler;

        const extractContext = function(vm)
        {
            const options = typeof vm === 'function' && vm.cid != null ? vm.options : vm._isVue ? vm.$options || vm.constructor.options : vm || {};
            const name = options.name || options._componentTag;
            const file = options.__file;

            return {
                isRoot:         vm.$root === vm,
                name:           name,
                props:          options.propsData,
                parentVnodeTag: options._parentVnode ? options._parentVnode.tag : undefined,
                file:           file,
            };
        };

        if(import.meta.env.MODE == 'production')
        {
            app.config.errorHandler = (error, vm, info) =>
            {
                this._honeybadger.notify(error, { context: { vm: extractContext(vm), info: info } });

                if(typeof chainedErrorHandler === 'function')
                {
                    chainedErrorHandler.call(app, error, vm, info);
                }
            };
        }
    }

    notify(error)
    {
        this._honeybadger.notify(error);
    }

    /**
     * @param {(notice?: import('@honeybadger-io/core').Types.Notice) => boolean | void} handler
     *        Called before each notice is reported; return `false` to skip reporting it.
     */
    beforeNotify(handler)
    {
        this._honeybadger.beforeNotify(handler);
    }

    setContext(context)
    {
        this._honeybadger.setContext(context);
    }

    addBreadcrumb(message, opts)
    {
        this._honeybadger.addBreadcrumb(message, opts);
    }

    clear()
    {
        this._honeybadger.clear();
    }

    getVersion()
    {
        this._honeybadger.getVersion();
    }
};

/**
 * @returns {Honeybadger}
 */
export function useHoneybadger()
{
    return inject('honeybadger');
}
